/**
 * What the panel settles into after a handoff — the reverse endpoint of the
 * same `layoutId` FLIP that turned the orb into the panel, so the collapse
 * reads as the same object shrinking down rather than a new element
 * appearing. Her intro line rides along as a pinned caption above it (not
 * itself part of the shared-element geometry — see MahiChatPanel for why —
 * but styled and timed to read as the same line surviving the collapse).
 */
import React from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import useReducedMotion from '../../hooks/useReducedMotion';

export default function MahiResultsChip({ layoutId, label, caption, onReopen, onClear }) {
  const reduced = useReducedMotion();

  return (
    <div className="px-4 pt-2 pb-2 flex flex-col items-center gap-1.5" data-testid="mahi-results-header">
      {caption && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="text-[12.5px] text-mahiteal-700 font-mahi-sans text-center max-w-xs leading-snug"
          data-testid="mahi-results-caption"
        >
          {caption}
        </motion.p>
      )}
      <motion.div
        layoutId={reduced ? undefined : layoutId}
        initial={reduced ? { opacity: 0 } : false}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{ borderRadius: 9999 }}
        transition={reduced ? { duration: 0.18, ease: 'easeInOut' } : { type: 'spring', stiffness: 260, damping: 28, mass: 0.9 }}
        className="inline-flex items-center gap-1.5 bg-white ring-1 ring-mahiteal-200 shadow-[0_8px_20px_-10px_rgba(29,162,132,0.5)]"
      >
        <button
          type="button"
          onClick={onReopen}
          data-testid="mahi-results-chip"
          aria-label={`Refine "${label}" with MAHI`}
          className="pl-3.5 pr-1 py-1.5 text-[13px] font-medium text-mahiteal-800 font-mahi-sans active:scale-[0.97] transition-transform"
        >
          {label}
        </button>
        <button
          type="button"
          onClick={onClear}
          data-testid="mahi-results-clear"
          aria-label="Clear this search and return to the default feed"
          className="w-6 h-6 mr-1 rounded-full flex items-center justify-center text-mahiteal-500 hover:bg-mahiteal-50 active:scale-90 transition-transform"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      </motion.div>
    </div>
  );
}
